import {
  parseActivePlanGrant,
  parseExpiredSignupGrant,
  parseLapsedSubscription,
  subscriptionLapsedReminderBody,
  trialExpiredReminderBody,
  type ActivePlanGrant,
  type LapsedSubscription,
} from '../../../../../shared/billing-catalog';
import { normalizePaidPlanCode, type PaidPlanCode } from '../feature-flags/planIntentService';
import type { PlanEntitlementsRow } from '../feature-flags/domain/planEntitlements';
import type { PaidTier } from './billingScreen.utils';

export type BillingReminderState = {
  signupTrialReminder: { visible: boolean; body: string | null };
  subscriptionLapsedReminder: { visible: boolean; body: string | null };
  highlightedPlan: PaidPlanCode | null;
  expiredSignupGrant: ActivePlanGrant | null;
  lapsedSubscription: LapsedSubscription | null;
};

export function resolveBillingReminderState(input: {
  entitlements: PlanEntitlementsRow | null | undefined;
  subscribedTier: PaidTier;
  pendingPlan?: PaidPlanCode | null;
}): BillingReminderState {
  const { entitlements, subscribedTier, pendingPlan = null } = input;
  const isOwner = entitlements?.is_owner === true;
  const activePlanGrant = parseActivePlanGrant(entitlements?.active_plan_grant);
  const expiredSignupGrant = parseExpiredSignupGrant(entitlements?.expired_signup_grant);
  const lapsedSubscription = parseLapsedSubscription(entitlements?.lapsed_subscription);

  const onGratisWithoutGrant = subscribedTier === 'gratis' && !activePlanGrant;
  const hasPendingPlan = pendingPlan != null;

  const subscriptionLapsedVisible = Boolean(
    isOwner && onGratisWithoutGrant && lapsedSubscription && !hasPendingPlan,
  );
  const subscriptionLapsedReminder = {
    visible: subscriptionLapsedVisible,
    body: subscriptionLapsedVisible ? subscriptionLapsedReminderBody(lapsedSubscription) : null,
  };

  const signupTrialVisible = Boolean(
    isOwner &&
      onGratisWithoutGrant &&
      expiredSignupGrant &&
      !hasPendingPlan &&
      !subscriptionLapsedVisible,
  );
  const signupTrialReminder = {
    visible: signupTrialVisible,
    body: signupTrialVisible ? trialExpiredReminderBody(expiredSignupGrant) : null,
  };

  let highlightedPlan: PaidPlanCode | null = null;
  if (activePlanGrant?.plan_code) {
    // Trial activo: destacar el plan del grant para conversión.
    highlightedPlan = normalizePaidPlanCode(activePlanGrant.plan_code);
  } else if (pendingPlan) {
    highlightedPlan = pendingPlan;
  } else if (lapsedSubscription?.plan_code) {
    highlightedPlan = normalizePaidPlanCode(lapsedSubscription.plan_code);
  } else if (expiredSignupGrant?.plan_code) {
    highlightedPlan = normalizePaidPlanCode(expiredSignupGrant.plan_code);
  }

  return {
    signupTrialReminder,
    subscriptionLapsedReminder,
    highlightedPlan,
    expiredSignupGrant,
    lapsedSubscription,
  };
}
